/**
 * Smooths transient socket blips out of the *UI-facing* connection status.
 *
 * Socket.IO auto-reconnects after a drop (a backgrounded tab throttles the WS
 * ping/pong; infra may close idle frames), usually re-establishing within a few
 * seconds. Surfacing every such blip as "Not connected" — which greys out the
 * composer and any pending clarify / checkpoint card — makes a self-healing
 * reconnect read as a broken, stopped session. (Observed in the OR-6288 #4034
 * session: two ~2–7s reconnect blips during an idle wait left a live clarify
 * card greyed out, and the researcher typed "continue" instead of answering it.)
 *
 * Contract:
 *  - reflect ``connected`` immediately (and cancel any pending grace timer);
 *  - delay any transition AWAY from a previously-connected state by ``graceMs``;
 *    if the socket reconnects within the window the drop is never surfaced;
 *  - show the raw status verbatim BEFORE the first successful connect, so a
 *    cold-start "Connecting…" still appears.
 *
 * UI-only smoothing: the raw status still drives the subscribe/replay machinery
 * in useEpiChatPage, so a real reconnect always re-subscribes — this only
 * governs what the banner / disabled-gate render.
 */

import { useEffect, useRef, useState } from 'react';

import type { EpiSocketStatus } from './useEpiStudyAgentSocket';

export const RECONNECT_GRACE_MS = 4000;

export const useGracefulConnectionStatus = (
  rawStatus: EpiSocketStatus,
  graceMs: number = RECONNECT_GRACE_MS
): EpiSocketStatus => {
  const [uiStatus, setUiStatus] = useState<EpiSocketStatus>(rawStatus);
  // Whether the socket has ever reached ``connected`` — gates the grace window
  // so a cold-start "Connecting…" is shown verbatim, not held back.
  const hasConnectedRef = useRef(rawStatus === 'connected');
  // Always-current raw status, read when the grace timer fires (the timer is
  // armed once per outage and must reflect wherever the status has settled).
  const rawRef = useRef(rawStatus);
  rawRef.current = rawStatus;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (rawStatus === 'connected') {
      hasConnectedRef.current = true;
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      setUiStatus('connected');
      return;
    }
    // Before the first successful connect, surface the raw status directly.
    if (!hasConnectedRef.current) {
      setUiStatus(rawStatus);
      return;
    }
    // A blip after having been connected: hold the connected UI for the grace
    // window. Arm exactly one timer per outage — substatus flaps
    // (disconnected↔connecting) during the same outage must not restart it.
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      if (rawRef.current !== 'connected') setUiStatus(rawRef.current);
    }, graceMs);
  }, [rawStatus, graceMs]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  return uiStatus;
};
